import React from "react";
import { StyleSheet, View } from "react-native";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";
import { formatDurationFromSeconds } from "../../../utils/timeUtils";

interface CourseAnalyticsData {
  id: string;
  name: string;
  totalSessions: number;
  totalDuration: number;
  averageDuration: number;
  lastActiveDate: string | null;
  firstActiveDate: string | null;
}

interface CourseAnalyticsTableProps {
  courses: CourseAnalyticsData[];
}

const CourseAnalyticsTable: React.FC<CourseAnalyticsTableProps> = ({ courses }) => {
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={[styles.headerCell, styles.courseNameCell]}>
          <CustomText text="Course Name" size="s" weight="medium" style={styles.headerText} />
        </View>
        <View style={styles.headerCell}>
          <CustomText text="Total Sessions" size="s" weight="medium" style={styles.headerText} />
        </View>
        <View style={styles.headerCell}>
          <CustomText text="Total Duration" size="s" weight="medium" style={styles.headerText} />
        </View>
        <View style={styles.headerCell}>
          <CustomText text="Avg Duration" size="s" weight="medium" style={styles.headerText} />
        </View>
        <View style={styles.headerCell}>
          <CustomText text="First Active Date" size="s" weight="medium" style={styles.headerText} />
        </View>
        <View style={styles.headerCell}>
          <CustomText text="Last Active Date" size="s" weight="medium" style={styles.headerText} />
        </View>
      </View>

      {courses.map((course, index) => (
        <View key={course.id} style={[styles.row, index % 2 === 0 && styles.evenRow]}>
          <View style={[styles.cell, styles.courseNameCell]}>
            <CustomText text={course.name} size="s" style={styles.cellText} useTranslationText={false} />
          </View>
          <View style={styles.cell}>
            <CustomText
              text={course.totalSessions.toString()}
              size="s"
              style={styles.cellText}
              useTranslationText={false}
            />
          </View>
          <View style={styles.cell}>
            <CustomText
              text={formatDurationFromSeconds(course.totalDuration)}
              size="s"
              style={styles.cellText}
              useTranslationText={false}
            />
          </View>
          <View style={styles.cell}>
            <CustomText
              text={formatDurationFromSeconds(course.averageDuration)}
              size="s"
              style={styles.cellText}
              useTranslationText={false}
            />
          </View>
          <View style={styles.cell}>
            <CustomText
              text={formatDate(course.firstActiveDate)}
              size="s"
              style={styles.cellText}
              useTranslationText={false}
            />
          </View>
          <View style={styles.cell}>
            <CustomText
              text={formatDate(course.lastActiveDate)}
              size="s"
              style={styles.cellText}
              useTranslationText={false}
            />
          </View>
        </View>
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
  },
  header: {
    flexDirection: "row",
    backgroundColor: "#F9FAFB",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  headerCell: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  courseNameCell: {
    flex: 2,
    alignItems: "flex-start",
  },
  headerText: {
    color: "#6B7280",
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  row: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  evenRow: {
    backgroundColor: "#F9FAFB",
  },
  cell: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  cellText: {
    color: "#6B7280",
  },
});

export default CourseAnalyticsTable;
